/* eslint-disable sonarjs/cognitive-complexity */
/*
 * Copyright Firstlight Media Inc. All rights reserved.
 *
 * This software is the confidential and proprietary information of Firstlight Media Inc. and is protected by copyright
 * law. No license, implied or otherwise is granted by its use, unless licensed by Firstlight Media Inc.
 */
import { remove } from './ArrayUtils';
import { Factory } from '../../Factory';
import { createDictionary } from './ObjectUtils';
import { Logger } from '../../logger/Logger';

/**
 * A function that returns anything, and takes any arguments.
 * Useful for passing around callbacks in method signatures.
 * This is not type-safe at all, and where possible, functions
 * should enforce type safety in their expected callbacks.
 */
export type AnyFunc = (...args: any[]) => any;

export type eventObject = {
  [key: string]: any;
};

export interface EventSubscribeable {
  subscribe(eventName: string | eventObject, callback?: AnyFunc): void;
  unsubscribe(eventName: string | eventObject, callback?: AnyFunc): void;
}

export interface EventEmitter extends EventSubscribeable {
  publish(eventName: string, ...info: any[]): void;
  reset(): void;
}

/**
 * This is intended to be a mixin so that other objects can publish events.
 *
 * @param {object} [obj] If provided, then `createEventEmitter` will add `subscribe` and `unsubscribe`
 *    functions to this object.
 *
 * @constructs eventEmitter
 * @mixin
 */
export const createEventEmitter = (obj?: any): EventEmitter => {
  const logger: Logger = Factory.createlogger();

  let eventNames = createDictionary();

  /**
   * @lends eventEmitter.prototype
   */
  const emitter: EventEmitter = {
    /**
     * Register a callback to be called when a certain event is published
     *
     * @param {string|object} eventName Name of the event to subscribe to, or an object, with
     *    keys as strings and callbacks as values (if it is an object, the value of `listener`
     *    will be ignored.
     * @param {function} listener Callback that will be called when `eventName` is published
     */
    subscribe(eventName: string | eventObject, listener?: AnyFunc) {
      if (eventName && typeof eventName === 'object') {
        Object.keys(eventName).forEach((event: string) => {
          emitter.subscribe(event, eventName[event]);
        });
        return;
      }

      if (eventName && typeof eventName === 'string') {
        const listeners = eventNames[eventName] || [];
        if (listeners.includes(listener)) return;
        listeners.push(listener);
        eventNames[eventName] = listeners;
      }
    },

    /**
     * Publish an event to trigger all the callbacks registered against this event, and pass releveant data
     *
     * @param {string} eventName Name of the event to publish
     * @param {...*} info Any number of data to be passed to callbacks when `eventName` is published
     *
     * Note:If a callback throws, it will fail silently. Catch the error and log it.
     */
    publish(eventName: string, ...info: any[]) {
      if (!eventNames[eventName]) {
        return;
      }

      eventNames[eventName].forEach((callback: (...info: any[]) => any) => {
        try {
          callback(...info);
        } catch (error) {
          logger.error('Uncaught error in event listener', error);
          throw error;
        }
      });
    },

    /**
     * Remove an event listener. It will no longer be invoked when `eventName`
     * is published.
     *
     * @param {string} eventName
     * @param {function} listener
     */
    unsubscribe(eventName: string | eventObject, listener?: AnyFunc) {
      if (eventName && typeof eventName === 'object') {
        Object.keys(eventName).forEach((event: string) => emitter.unsubscribe(event, eventName[event]));

        return;
      }

      if (eventName && typeof eventName === 'string') {
        const listeners = eventNames[eventName];

        if (!listeners) {
          return;
        }

        remove(listeners, listener);
      }
    },

    /**
     * Remove all stored event listeners.
     */
    reset() {
      eventNames = createDictionary();
    },
  };

  if (obj) {
    obj.subscribe = emitter.subscribe;
    obj.unsubscribe = emitter.unsubscribe;
  }

  return emitter;
};
